import { useState } from "react";
import { useI18n } from "~/i18n";
import { SECTION_TOP_DIVIDER } from "~/components/sections/section-top-divider";
import { ModalCloseButton, ModalOverlay, ModalPanel } from "~/components/modal";

interface CtaPlatform {
  name: string;
  url: string;
  icon?: string;
  countries?: string;
}

interface CtaLink {
  title: Record<string, string>;
  label: Record<string, string>;
  image: string;
  bgColor: string;
  platforms?: CtaPlatform[];
  loading?: boolean;
}

interface CtaLinksProps {
  links: CtaLink[];
}

export function CtaLinks({ links }: CtaLinksProps) {
  const { locale, t } = useI18n();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const activeLink = activeIndex !== null ? links[activeIndex] : null;
  const isLoading = activeLink?.loading ?? false;

  return (
    <>
      <section className={`relative z-[2] bg-white py-[60px] ${SECTION_TOP_DIVIDER}`}>
        <div className="max-w-full mx-auto px-[clamp(24px,5vw,120px)]">
          <div className="mx-auto max-w-[900px]">
            <div className="grid grid-cols-2 gap-[15px] max-md:grid-cols-1 max-md:gap-3">
              {links.map((link, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className="group relative flex h-[96px] items-center overflow-hidden rounded-[16px] transition-opacity hover:opacity-90 cursor-pointer"
                  style={{ background: link.bgColor }}
                >
                  <img src={link.image} alt="" className="h-[96px] w-auto block" />
                  <div className="absolute inset-0 flex flex-col items-end justify-center gap-0 pr-8 text-white whitespace-nowrap">
                    <span className="text-[12px] font-light tracking-[0.04em]">
                      {link.title[locale] ?? link.title.ko}
                    </span>
                    <span className="text-[20px] max-md:text-[17px] font-bold tracking-[0.02em]">
                      {link.label[locale] ?? link.label.ko}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {activeLink && (
        <ModalOverlay onClose={() => setActiveIndex(null)}>
          <ModalPanel className="max-w-[420px]">
            <ModalCloseButton />

            {isLoading ? (
              <>
                {/* 스켈레톤: Header */}
                <div className="shrink-0 pt-[22px] px-8 pr-16 pb-4 max-md:pt-4 max-md:px-5 max-md:pr-14 max-md:pb-3">
                  <div className="skeleton h-[24px] w-[60%]" />
                </div>

                {/* 스켈레톤: Platform list */}
                <div className="px-5 pt-3 pb-8 max-md:px-4 max-md:pb-5 flex flex-col gap-3">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="rounded-[12px] border border-[#f0f0f0] px-5 py-4 flex flex-col gap-3"
                    >
                      <div className="flex items-center gap-2">
                        <div className="skeleton w-5 h-5 rounded-[3px]" />
                        <div className="skeleton h-[16px] w-[100px]" />
                      </div>
                      <div className="skeleton h-[14px] w-[80px] rounded" />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Header */}
                <div className="shrink-0 pt-[22px] px-8 pr-16 pb-4 max-md:pt-4 max-md:px-5 max-md:pr-14 max-md:pb-3">
                  <h2 className="text-[22px] max-md:text-[18px] font-bold leading-relaxed text-[var(--ip-text)]">
                    {activeLink.label[locale] ?? activeLink.label.ko}
                  </h2>
                </div>

                {/* Platform list */}
                <div className="px-5 pt-3 pb-8 max-md:px-4 max-md:pb-5 flex flex-col gap-3">
                  {(!activeLink.platforms || activeLink.platforms.length === 0) && (
                    <div className="flex items-center justify-center py-8 text-[15px] text-[#999]">
                      {t.cta.comingSoon}
                    </div>
                  )}
                  {(activeLink.platforms ?? []).map((platform, i) => (
                    <a
                      key={i}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="goods-store-card group flex flex-col gap-3 rounded-[12px] bg-white px-5 py-4 transition-all duration-300 hover:bg-[#fcfcfc]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {platform.icon ? (
                            <img
                              src={platform.icon}
                              alt=""
                              className="w-5 h-5 rounded-[3px] shrink-0 object-contain"
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-[3px] bg-[#f0f4f6] shrink-0" />
                          )}
                          <span className="text-[15px] font-bold text-[var(--ip-text)]">
                            {platform.name}
                          </span>
                        </div>
                        <span className="text-sm text-[#aaa] transition-transform group-hover:translate-x-1">
                          →
                        </span>
                      </div>
                      {platform.countries && (
                        <span className="text-[12px] text-[#999] leading-tight">
                          {platform.countries}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </>
            )}
          </ModalPanel>
        </ModalOverlay>
      )}
    </>
  );
}
